import { useState } from "react";
import { TextField, Button, Select, MenuItem, Typography, CircularProgress } from "@mui/material";
import authService from "../../services/authService";
import { showSnackbar } from "../Snackbar/snackbarManager";
import GenieLogo from "../../public/logo/genie_text.svg?url";
import AnarockLogo from "../../public/logo/anarock_logo.svg?url";
import { grayDark, gray600, black, grayLight, violet700, violetDark, gray300 } from "@/constants/colors";
import { isValidNumericInput } from "@/utils/commonFunctions";
import OTPInput from "../OTPInput";
import OTPResendTimer from "../OTPResendTimer";
import { isAxiosError } from "axios";
import { useRouter } from "next/router";
import { redirectAfterAuth, resolvePostLoginRedirect } from "@/utils/authRedirectUtils";
import { User } from "@/types/types";
import { useCountries } from "@/features/metadata/useMetadataQueries";
import { useVerifyOtp } from "@/features/auth/useAuthMutations";

const isEmptyString = (str: string) => str === "";
const handleError = (error: unknown): void => {
  const errorMessages = {
    offline: "No internet connection. Please check and retry.",
    network: "Service unreachable. Please try again later.",
    default: "Something went wrong. Please try again",
  };

  let message = errorMessages.default;

  if (!navigator.onLine) {
    message = errorMessages.offline;
  } else if (isAxiosError(error) && error.response) {
    message =
      error.response.data?.message ||
      error.response.data?.results?.[0]?.message ||
      (error.code === "ERR_NETWORK" ? errorMessages.network : errorMessages.default);
  } else if (error instanceof Error) {
    message = error.message;
  }

  showSnackbar(message, { type: "info" });
};

interface disableSubmitParams {
  otp: string;
  phoneNumber: string;
  otpSent: boolean;
}

const disableSubmit = ({ otp, otpSent, phoneNumber }: disableSubmitParams) => {
  if (otpSent) {
    return otp.length < 4;
  }
  return isEmptyString(phoneNumber);
};
export default function Login() {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [countryCode, setCountryCode] = useState("+91");
  const [isLoading, setIsLoading] = useState(false);
  const router = useRouter();

  const isSubmitDisabled =
    disableSubmit({
      otp,
      phoneNumber,
      otpSent,
    }) || isLoading;

  const submitButtonText = otpSent ? "Login" : "Send OTP";
  const { data: countries } = useCountries();
  const { mutateAsync: verifyOtp } = useVerifyOtp();

  const handleSubmit = async () => {
    if (!otpSent) {
      await handleSendOtp();
    } else {
      await handleVerifyOtp();
    }
  };

  const handleRedirect = (user: User) => {
    if (user === null) {
      return;
    }

    const destination = resolvePostLoginRedirect({
      returnUrl: router.query.return_url,
      rt: router.query.rt,
      query: router.query,
    });
    redirectAfterAuth(destination);
  };

  const handleSendOtp = async () => {
    try {
      setIsLoading(true);
      const selectedCountry = countries?.find(country => country.country_code === countryCode);
      await authService.sendOtp({
        phone: phoneNumber,
        via_call: false,
        country_id: selectedCountry?.id || 49,
      });
      setOtpSent(true);
    } catch (error) {
      handleError(error);
      console.error("Error sending OTP:", error);
    } finally {
      setIsLoading(false);
    }
  };
  const handleVerifyOtp = async () => {
    try {
      setIsLoading(true);
      const selectedCountry = countries?.find(country => country.country_code === countryCode);
      const response = await verifyOtp({
        phone: phoneNumber,
        country_id: selectedCountry?.id || 4,
        via_call: false,
        otp: otp,
      });
      handleRedirect(response.user);
    } catch (error) {
      setIsLoading(false);
      handleError(error);
      console.error("Error verifying OTP:", error);
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === "" || isValidNumericInput(value)) setPhoneNumber(value);
  };

  return (
    <div
      className={`flex min-h-[100svh] w-screen flex-col items-center sm:justify-center`}
      style={{ backgroundColor: black }}
    >
      <img className="mt-4 w-20 sm:w-[6.25rem]" src={GenieLogo.src} alt="Genie" />

      <form
        onSubmit={e => {
          e.preventDefault();
          handleSubmit();
        }}
        className="my-8 rounded-lg p-3 text-left shadow-lg sm:p-6"
        style={{
          width: "24.25rem",
          height: "auto",
          maxWidth: "95%",
          backgroundColor: grayDark,
        }}
      >
        <div className="mb-6">
          <Typography
            sx={{
              fontWeight: "600",
              letterSpacing: "-0.24px",
              marginBottom: "0.375rem",
            }}
            variant="h5"
          >
            Welcome to genie.
          </Typography>
          <Typography
            sx={{
              color: gray600,
              fontWeight: "400",
              lineHeight: 1.25,
              letterSpacing: "-0.14px",
            }}
          >
            Sign in to continue
          </Typography>
        </div>

        {otpSent && (
          <div className="mb-4 flex justify-between text-sm">
            <div>{countryCode + "-" + phoneNumber}</div>
            <a
              style={{ color: violet700 }}
              className="cursor-pointer hover:underline focus:underline"
              onClick={() => {
                setOtp("");
                setOtpSent(false);
              }}
            >
              Edit
            </a>
          </div>
        )}

        {!otpSent ? (
          <div className="flex items-center gap-2">
            <Select
              labelId="country-code-label"
              id="country-code-select"
              value={countryCode}
              onChange={e => {
                setCountryCode(e.target.value);
              }}
              disabled={otpSent}
              sx={{
                margin: "1rem 0 0.5rem",
                "& div": {
                  padding: "0.5rem 0.875rem",
                },
              }}
              inputProps={{ color: "white" }}
            >
              {countries?.map(country => (
                <MenuItem key={country.id} value={country.country_code}>
                  {country.country_code}
                </MenuItem>
              ))}
            </Select>
            <TextField
              label="Phone Number"
              type="tel"
              value={phoneNumber}
              onChange={handlePhoneChange}
              fullWidth
              margin="normal"
              disabled={otpSent}
              size="small"
            />
          </div>
        ) : (
          <div className="my-2 flex items-center justify-between">
            <OTPInput value={otp} length={4} onChange={setOtp} />
            <OTPResendTimer resendOTP={handleSendOtp} />
          </div>
        )}

        <Button
          type="submit"
          variant="contained"
          fullWidth
          sx={{
            marginTop: "1rem",
            marginBottom: "0.5rem",
            textTransform: "capitalize",
            "&:disabled": {
              backgroundColor: violetDark,
            },
          }}
          disabled={isSubmitDisabled}
        >
          {isLoading ? (
            <CircularProgress
              size={24}
              style={{
                color: gray300,
              }}
            />
          ) : (
            submitButtonText
          )}
        </Button>
      </form>

      <div className="flex flex-col items-center gap-[10px]">
        <Typography variant="caption" sx={{ color: grayLight }}>
          Powered by
        </Typography>
        <img src={AnarockLogo.src} className="w-[90px]" alt="Anarock" />
      </div>
    </div>
  );
}
